import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { findPlan } from "@/lib/plans";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PromoCodeField, type AppliedPromo } from "@/components/PromoCodeField";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/checkout/$priceId")({
  component: CheckoutPage,
  head: () => ({
    meta: [
      { title: "Start your free trial — AdKavi" },
      { name: "description", content: "Start your 14-day free trial. Card required, cancel anytime." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function CheckoutPage() {
  const { priceId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const plan = findPlan(priceId);
  const [promo, setPromo] = useState<AppliedPromo | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!plan) {
      navigate({ to: "/pricing" });
      return;
    }
    if (!user) {
      // Stash the plan so /auth knows where to send them after signup.
      try {
        sessionStorage.setItem("adkavi:pendingPlan", priceId);
      } catch {
        // ignore
      }
      navigate({ to: "/auth" });
    }
  }, [user, loading, plan, priceId, navigate]);

  if (loading || !user || !plan) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("checkout.loading")}
      </main>
    );
  }

  const returnUrl = `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <header className="border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> {t("checkout.changePlan")}
          </Link>
          <div className="text-sm">
            <span className="text-muted-foreground">{user.email}</span>
          </div>
        </div>
      </header>
      <section className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-10">
        <div>
          <h1 className="text-2xl font-bold mb-1">{t("checkout.trialHeading")}</h1>
          <p className="text-sm text-muted-foreground mb-4">
            {t("checkout.trialDesc")}
          </p>
          <div className="mb-4">
            <PromoCodeField userId={user.id} applied={promo} onApply={setPromo} />
          </div>
          <div className="rounded-2xl border border-border/50 bg-card/40 p-2">
            <StripeEmbeddedCheckout
              priceId={priceId}
              customerEmail={user.email ?? undefined}
              userId={user.id}
              returnUrl={returnUrl}
              promotionCodeId={promo?.promotionCodeId ?? null}
              promoCode={promo?.code ?? null}
            />
          </div>
        </div>
        <aside className="rounded-2xl border border-border/50 bg-card/40 p-6 h-fit">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
            {t("checkout.youreStarting")}
          </p>
          <h2 className="text-xl font-bold">{plan.name}</h2>
          <div className="flex items-baseline gap-1 mt-1 mb-4">
            <span className="text-3xl font-bold">${plan.price.toLocaleString()}</span>
            <span className="text-sm text-muted-foreground">{t("checkout.perMonthAfterTrial")}</span>
          </div>
          <ul className="space-y-2 text-sm">
            {plan.features.map((f) => (
              <li key={f} className="text-muted-foreground">
                — {f}
              </li>
            ))}
          </ul>
        </aside>
      </section>
    </div>
  );
}
